import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { MenuModule } from 'primeng/menu';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { takeUntil } from 'rxjs';

import { FilesDddService } from '@src/app/services/api/files-ddd.service';
import { Destroyable } from '@src/app/utils/Destroyable';
import { IAddTachographCompanyCardBody, ICompanyCard } from '@src/app/interfaces/api/files-ddd.interface';
import { NotificationService } from '@commons/services/notification/notification.service';

@Component({
  selector: 'app-tachograf-config',
  standalone: true,
  imports: [CommonModule, ButtonModule, TableModule, MenuModule, FormsModule, ReactiveFormsModule, TranslateModule, TooltipModule, DialogModule, InputTextModule, FormsModule],
  templateUrl: './tachograf-config.component.html',
  styleUrls: ['./tachograf-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TachografConfigComponent extends Destroyable implements OnInit {

  private readonly filesDDDService = inject(FilesDddService);
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);

  public visibleForm = false;
  public formCard = new FormGroup<any>({
    DriverCardReadDaysInterval: new FormControl('', [Validators.required]),
    TachographMemoryReadDaysInterval: new FormControl('', [Validators.required]),
  });

  public addCompanyCardForm = new FormGroup({
    Name: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    Number: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
  });

  public cards = signal<ICompanyCard[]>([]);
  public selectedCard: ICompanyCard;
  public isSaveingSettings = signal(false);

  public readonly cardsListAction: MenuItem[] = [
    {
      label: this.translate.instant('options'),
      items: [
        {
          label: this.translate.instant('areas.edit'),
          icon: 'pi pi-pencil',
          command: () => {
            this.updateCompanyCardForm(this.selectedCard);
          }
        },
        {
          label:  this.translate.instant('areas.delete'),
          icon: 'pi pi-trash',
          command: () => {
            this.deleteCompanyCardFromList(this.selectedCard.Id);
            this.deleteCompanyCard(this.selectedCard);
          }
        },
      ],
    }
  ];

  ngOnInit(): void {
    this.getTachoConfig();
    this.getCompanyCards();
  }

  private updateCompanyCardForm(data: ICompanyCard): void {
    this.addCompanyCardForm.patchValue({
      Name: data.Name,
      Number: data.Number,
    });
    this.visibleForm = true;
  }

  private getCompanyCards(): void {
    this.filesDDDService.GetTachographCompanyCards().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.cards.set(res.Cards);
      }
    });
  }

  public saveFormCompanyCard(): void {
    if (this.addCompanyCardForm.valid) {
      const data = {
        CompanyCard: {
          ...this.addCompanyCardForm.getRawValue(),
          Id: this.selectedCard?.Id,
        },
      };

      if (this.selectedCard) {
        this.filesDDDService.UpdateTachographCompanyCard(data).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.updateCard(this.selectedCard.Id, data.CompanyCard);
            this.hideFormNewCard();
          },
        });
        return;
      }

      this.filesDDDService.AddTachographCompanyCard(data).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.addNewCard(data, res.Id);
          this.hideFormNewCard();
        },
      });
    }
  }

  public hideFormNewCard(): void {
    this.visibleForm = false;
    this.addCompanyCardForm.reset();
  }

  public deleteCompanyCard(data: ICompanyCard): void {
    this.filesDDDService.DeleteTachographCompanyCard(data.Id).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private deleteCompanyCardFromList(Id: number): void {
    this.cards.update(cards => cards.filter(card => card.Id !== Id));
    this.notificationService.showMessage(this.translate.instant('cardActionDelete'), this.translate.instant('companyCardVehicle'));
  }

  public updateCard(Id: number, payload: { Name: string; Number: string }): void {
    this.cards.update(cards =>
      cards.map(card =>
        card.Id === Id
          ? { ...card, ...payload }
          : card
      )
    );
    this.notificationService.showMessage(this.translate.instant('cardActionUpdate'), this.translate.instant('companyCardVehicle'));
  }

  private addNewCard({ CompanyCard }: IAddTachographCompanyCardBody, Id: number): void {
    this.cards.update(cards => [
      ...cards,
      {
        Name: CompanyCard.Name,
        Number: CompanyCard.Number,
        Id,
      }
    ]);
    this.notificationService.showMessage(this.translate.instant('cardActionAdded'), this.translate.instant('companyCardVehicle'));
  }

  public saveSettings(): void {
    if (this.formCard.valid) {
      this.isSaveingSettings.set(true);

      const body = this.formCard.value as any;
      this.filesDDDService.SetTachographConfig(body).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.isSaveingSettings.set(false);
          this.notificationService.showMessage(this.translate.instant('formWasSaved'), this.translate.instant('downloadSettings'));
        }
      });
    }
  }

  private getTachoConfig(): void {
    this.filesDDDService.GetTachographConfig().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => this.formCard.patchValue(data),
    });
  }

}
